// Ops desk modules, row 3: the sanity-gate donut and the risk monitor (span 3),
// then the models, goals and operator faces (span 2). Reads go through
// opsModuleHooks, which share the key/queryFn/refetchInterval of the panel behind
// each drawer, so opening the drawer is a cache hit. Faces are state-only: the
// whole module is the button, so nothing clickable lives inside (the operator
// controls are all in the drawer, ruling 46).
//
// Loading rule (see fleetModuleHooks): `stillLoading` shows the skeleton, a query
// that failed with nothing to show gets the error rung, and a failed REFETCH keeps
// its cached data. An unknown is a dash, never a zero or a reassuring word.
import type { UseQueryResult } from '@tanstack/react-query'
import { useSanityGateBuffer } from '../../hooks/useSanityGateBuffer'
import { useNow } from '../../hooks/useNow'
import { relativeTime } from '../../lib/relativeTime'
import { ArcGauge } from '../viz/ArcGauge'
import { Donut } from '../viz/Donut'
import { ProgressBar } from '../viz/ProgressBar'
import { Module } from './Module'
import { CLOCK_MS } from './bookModuleHooks'
import { DASH, stillLoading, unavailable } from './fleetModuleHooks'
import {
  useAdaptiveStatus,
  useCircuitBreaker,
  useControlsStatus,
  useEngineStatus,
  useEnsemble,
  useGoals,
  useLLMEnvelope,
  useRiskMetrics,
} from './opsModuleHooks'
import { ensembleFace, gateSlices, goalRows, operatorRows, peakRow, riskRows, riskTone } from './opsSummaries'
import './OpsModules.css'

// -------------------------------------------------------------- sanity-gate

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/**
 * Decision mix of the session buffer (ruling 49). The ring's slice values are
 * shares in percent (the legend prints them as such); the event counts ride
 * along for the legend's extra column and the centre label.
 */
export function SanityGateModule() {
  const events = useSanityGateBuffer()
  const total = events.length
  const slices = gateSlices(events).map((s) => ({
    ...s,
    count: s.value,
    value: (s.value / total) * 100,
  }))
  const detail = slices.map((s) => `${s.count} ${s.label}`).join(', ')

  return (
    <Module
      title="Sanity gate"
      caption="since page load"
      drawerKey="sanity-gate"
      span={3}
      isEmpty={total === 0}
      emptyText="no gate events since page load"
    >
      <Donut
        slices={slices}
        centerLabel={String(total)}
        centerSub="events"
        legend
        legendExtra={(s) => <span className="ops-gate-count">{s.count}</span>}
        ariaLabel={`sanity gate: ${plural(total, 'event')}, ${detail}`}
      />
    </Module>
  )
}

// ------------------------------------------------------------- risk-monitor

const CHURN_FOOT = 'churn throttle always-allow · no writer'

/**
 * The default book's limits (ruling 50): the gauge shows the limit nearest (or
 * past) its cap, the list shows every row the API reports. Exposure is listed
 * but is not a limit (Ruling J), so it never peaks the gauge. Only limits the
 * API sends are drawn; the panel's fallback limits are never used here. A
 * triggered circuit breaker is a warn first row (ruling B); the gauge and the
 * peak read the metric rows only.
 */
export function RiskMonitorModule() {
  const metricsQ = useRiskMetrics()
  const breakerQ = useCircuitBreaker()
  const rows = riskRows(metricsQ.data)
  const peak = peakRow(rows)
  const tripped = breakerQ.data?.status === 'triggered'
  const peakPct = peak ? Math.round(peak.ratio * 100) : null

  return (
    <Module
      title="Risk monitor"
      caption="default book"
      drawerKey="risk-monitor"
      span={3}
      isLoading={stillLoading(metricsQ)}
      isError={unavailable(metricsQ)}
      errorText="risk unavailable"
    >
      <div className="ops-risk">
        <div className="ops-risk-gauge">
          <ArcGauge
            value={peak ? peak.ratio * 100 : null}
            max={100}
            tone={peak ? riskTone(peak.ratio) : 'brand'}
            label={peakPct === null ? DASH : `${peakPct}%`}
            sub={peak?.label}
            ariaLabel={peak ? `peak ${peak.label} ${peakPct}% of limit` : 'no limits reported'}
          />
        </div>
        <div className="ops-risk-rows">
          {tripped && (
            <p className="ops-risk-row ops-risk-row--breaker warn">
              <span className="ops-risk-label">circuit breaker triggered</span>
            </p>
          )}
          {rows.map((r) => (
            <p className="ops-risk-row" key={r.label}>
              <span className="ops-risk-label">{r.label}</span>
              <span className={`ops-risk-text${r.isLimit && r.ratio > 1 ? ' ops-risk-over' : ''}`}>{r.text}</span>
            </p>
          ))}
          {peak === null && <p className="ops-risk-none">no limits reported</p>}
        </div>
      </div>
      <p className="module-foot">{CHURN_FOOT}</p>
    </Module>
  )
}

// ------------------------------------------------------------------- models

/**
 * The recommendation word breaks at its spaces (STRONG SELL, PIPELINE OFF), so CSS
 * sizes it from its longest line and its line count rather than its full length.
 */
const wordSizing = (word: string) => {
  const parts = word.split(' ')
  return {
    ['--chars' as string]: Math.max(...parts.map((p) => p.length)),
    ['--lines' as string]: parts.length,
  }
}

/** The blended ML recommendation, how far the providers agree, and how many there are. */
export function ModelsModule() {
  useNow(CLOCK_MS) // the foot's age ticks even when a refetch returns identical data
  const q = useEnsemble()
  const face = ensembleFace(q.data)

  return (
    <Module
      title="Models"
      drawerKey="models"
      span={2}
      isLoading={stillLoading(q)}
      isError={unavailable(q)}
      errorText="models unavailable"
      isEmpty={face === null}
      emptyText="no ensemble reported"
    >
      {face && (
        <>
          <div className="ops-models">
            <p
              className={`ops-models-word${face.tone === 'warn' ? ' warn' : ''}`}
              style={wordSizing(face.word)}
            >
              {face.word}
            </p>
            <p className="ops-models-detail" style={{ ['--chars' as string]: face.detail.length }}>
              {face.detail}
            </p>
            {face.agreement !== null && (
              <div className="ops-models-bar">
                <ProgressBar
                  value={face.agreement}
                  max={1}
                  tone="brand"
                  ariaLabel={`agreement ${Math.round(face.agreement * 100)}%`}
                />
              </div>
            )}
          </div>
          <p className="module-foot">
            {plural(face.providers, 'provider')} · {relativeTime(face.updated)}
          </p>
        </>
      )}
    </Module>
  )
}

// -------------------------------------------------------------------- goals

const GOALS_SHOWN = 3

/** Up to three active goals as name / progress text / neutral bar (ruling 45). */
export function GoalsModule() {
  const q = useGoals()
  const rows = goalRows(q.data)
  const shown = rows.slice(0, GOALS_SHOWN)
  const more = rows.length - shown.length

  return (
    <Module
      title="Goals"
      drawerKey="goals"
      span={2}
      isLoading={stillLoading(q)}
      isError={unavailable(q)}
      errorText="goals unavailable"
      isEmpty={q.data !== undefined && rows.length === 0}
      emptyText="no active goals"
    >
      <div className="ops-goals">
        {shown.map((g, i) => (
          <div className="ops-goal" key={`${g.name}-${i}`}>
            <p className="ops-goal-name" title={g.name}>
              {g.name}
            </p>
            <p className={`ops-goal-text${g.negative ? ' ops-goal-neg' : ''}`}>{g.text}</p>
            {g.ratio !== null && (
              <ProgressBar value={g.ratio} max={1} tone="brand" ariaLabel={`${g.name} ${g.text}`} />
            )}
          </div>
        ))}
      </div>
      {more > 0 && <p className="module-foot">+{more} more</p>}
    </Module>
  )
}

// ----------------------------------------------------------------- operator

/** A source is "settled" once it has answered or failed; a failed one reads as a dash. */
const settled = (q: UseQueryResult<unknown>) => !stillLoading(q)

/**
 * The four state rows (ruling 46). Every control lives in the drawer. A source
 * that failed makes only its own row a dash; the error rung needs all four down.
 */
export function OperatorModule() {
  const engineQ = useEngineStatus()
  const controlsQ = useControlsStatus()
  const adaptiveQ = useAdaptiveStatus()
  const envelopeQ = useLLMEnvelope()
  const sources = [engineQ, controlsQ, adaptiveQ, envelopeQ]
  const rows = operatorRows(engineQ.data, controlsQ.data, adaptiveQ.data, envelopeQ.data)

  return (
    <Module
      title="Operator"
      drawerKey="operator"
      span={2}
      isLoading={!sources.some(settled)}
      isError={sources.every(unavailable)}
      errorText="operator unavailable"
    >
      <div className="ops-op">
        {rows.map((r) => (
          <p className="ops-op-row" key={r.label}>
            <span className="ops-op-label">{r.label}</span>
            <b className={`ops-op-word${r.tone === 'neutral' ? '' : ` ops-op-word--${r.tone}`}`}>{r.word}</b>
          </p>
        ))}
      </div>
    </Module>
  )
}
